import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContracts';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errorParser';
import { fmtUSDT } from '../../utils/format';
import { USDT_DECIMALS, CONTRACT_ADDRESSES } from '../../config/contracts';
import AdminSection from './AdminSection';
import ButtonLoader from '../common/ButtonLoader';

const AdminEmergency = ({ role, refreshKey, onChanged }) => {
  const { plans, usdt } = useContracts();
  const notify = useNotify();
  const [balance, setBalance] = useState('0');
  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  // Read USDT balance directly from the USDT contract — doesn't rely on
  // Plans having a getContractBalance() view.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!usdt?.read || !CONTRACT_ADDRESSES.PLANS) return;
      try {
        const b = await usdt.read.balanceOf(CONTRACT_ADDRESSES.PLANS);
        if (!cancelled) setBalance(b.toString());
      } catch (e) {
        console.warn('[AdminEmergency] balance read failed:', e);
        if (!cancelled) setBalance('0');
      }
    };
    load();
    return () => { cancelled = true; };
  }, [usdt, refreshKey]);

  const setMax = () => {
    setAmount(fmtUSDT(balance).replace(/,/g, ''));
  };

  const onWithdraw = async () => {
    if (!plans?.write) return notify.error('Wallet', 'Connect wallet first');
    if (!ethers.utils.isAddress(to)) return notify.error('Invalid', 'Bad recipient address');
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      return notify.error('Invalid', 'Enter amount > 0');
    }
    if (!confirm) return notify.error('Confirm', 'Tick the confirmation checkbox first');

    setBusy(true);
    try {
      const amt = ethers.utils.parseUnits(String(amount), USDT_DECIMALS);
      const tx = await plans.write.emergencyWithdraw(to, amt);
      notify.info('Submitting', 'Emergency withdraw sent…');
      await tx.wait();
      notify.success('Withdrawn', 'Emergency withdraw complete');
      setAmount('');
      setConfirm(false);
      onChanged && onChanged();
    } catch (e) {
      notify.error('Failed', parseError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminSection
      title="Emergency Withdraw"
      subtitle="Owner-only escape hatch. Pulls any USDT from the Plans contract — bypasses missing-pool / pool-fund accounting."
      ownerOnly
      role={role}
    >
      <div className="emergency-tile">
        <div className="emergency-warning">
          ⚠ Use only for stuck funds, accidental transfers, or contract retirement. This bypasses all internal accounting.
        </div>

        <div className="emergency-stats">
          <div>
            <div className="card-sub">Plans Contract Balance</div>
            <div className="card-value yellow">{fmtUSDT(balance)}</div>
            <div className="card-sub">USDT total (includes pool funds, missing pool, held income reserves)</div>
          </div>
        </div>

        <div className="admin-form" style={{ marginTop: 14 }}>
          <div className="admin-form-row">
            <label>Recipient Address</label>
            <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="0x…" />
          </div>
          <div className="admin-form-row">
            <label>Amount (USDT)</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="number"
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                style={{ flex: 1 }}
              />
              <button type="button" className="btn btn-ghost btn-sm" onClick={setMax} style={{ whiteSpace: 'nowrap' }}>
                MAX
              </button>
            </div>
          </div>

          <label className="emergency-confirm">
            <input
              type="checkbox"
              checked={confirm}
              onChange={(e) => setConfirm(e.target.checked)}
            />
            <span>I understand this withdraws raw USDT and may break user claims.</span>
          </label>

          <button
            className="btn btn-block"
            style={{
              marginTop: 4,
              background: confirm ? 'var(--error)' : 'var(--bg-charcoal-4)',
              color: confirm ? '#ffffff' : 'var(--text-muted)',
              fontWeight: 700
            }}
            onClick={onWithdraw}
            disabled={busy || !confirm}
          >
            {busy ? <ButtonLoader label="Withdrawing" /> : 'Emergency Withdraw'}
          </button>
        </div>
      </div>
    </AdminSection>
  );
};

export default AdminEmergency;
